<script lang="ts" setup>
import { XIcon, XButton } from '@clicksign/excalibur';
import { useI18n } from '#i18n';

import { BrandButton } from '~~base-ui';

import WhyPixModal from '../WhyPixModal/WhyPixModal.vue';
import PixTutorial1 from '../../assets/svg/pix/pix-tutorial-1.svg?component';
import PixTutorial2 from '../../assets/svg/pix/pix-tutorial-2.svg?component';
import PixTutorial3 from '../../assets/svg/pix/pix-tutorial-3.svg?component';

type PixTutorialEmit = {
  (event: 'finish'): void;
};

const { t } = useI18n();
const gtm = useGtm();
const emit = defineEmits<PixTutorialEmit>();

const activeIndex = ref(1);
const showWhyPixModal = ref(false);
const items = [
  {
    title: t('requirement.pix.tutorial.confirm.title'),
    subtitle: t('requirement.pix.tutorial.confirm.subtitle'),
    illustration: markRaw(PixTutorial1),
  },
  {
    title: t('requirement.pix.tutorial.pay.title'),
    subtitle: t('requirement.pix.tutorial.pay.subtitle'),
    illustration: markRaw(PixTutorial2),
  },
  {
    title: t('requirement.pix.tutorial.sign.title'),
    subtitle: t('requirement.pix.tutorial.sign.subtitle'),
    illustration: markRaw(PixTutorial3),
  },
];

const actionButtonText = computed(() => (activeIndex.value < items.length
  ? t('common.next')
  : t('requirement.pix.tutorial.sign.action')));

const activeItem = computed(() => items[activeIndex.value - 1]);

function goToPreviousIndex() {
  if (activeIndex.value > 1) {
    activeIndex.value -= 1;
  }
}

function goToNextIndex() {
  if (activeIndex.value < items.length) {
    activeIndex.value += 1;
  } else {
    gtm?.trackEvent({
      event: 'ga-send',
      ga_event_category: 'pix-step-widget-app',
      ga_event_name: 'signer-pix-action',
      ga_event_label: 'start_pix_auth',
      ga_options: 'start_pix_auth',
    });
    emit('finish');
  }
}

</script>

<template>
  <section
    data-testid="substepPixTutorial"
    :class="$style.pixTutorial"
  >
    <div :class="$style.carousel">
      <div :class="$style.carouselImages">
        <XButton
          :class="[$style.navButton, $style.prev]"
          :disabled="activeIndex === 1"
          model="icon"
          data-testid="previousButton"
          @click="goToPreviousIndex"
        >
          <XIcon icon="chevron-left" />
        </XButton>

        <Component
          :is="activeItem?.illustration"
          width="224"
        />

        <XButton
          :class="[$style.navButton, $style.next]"
          :disabled="activeIndex === items.length"
          model="icon"
          data-testid="nextButton"
          @click="goToNextIndex"
        >
          <XIcon icon="chevron-right" />
        </XButton>
      </div>

      <div :class="$style.carouselFooter">
        <h3>
          {{ activeItem?.title }}
        </h3>

        <p>{{ activeItem?.subtitle }}</p>

        <div :class="$style.dotsContainer">
          <div
            v-for="(item, index) in items"
            :key="index"
            :class="[$style.dot, activeIndex === index + 1 && $style.activeDot]"
          />
        </div>
      </div>
    </div>

    <BrandButton
      :class="$style.actionButton"
      full-width
      data-testid="nextSubstepButton"
      @click="goToNextIndex"
    >
      {{ actionButtonText }}

      <XIcon
        icon="arrow-right"
        right
      />
    </BrandButton>

    <XButton
      :class="$style.helpButton"
      data-testid="whyPixHelpLink"
      design="link"
      @click="showWhyPixModal = true"
    >
      {{ $t('requirement.pix.tutorial.whyPix.action') }}
    </XButton>

    <WhyPixModal
      v-if="showWhyPixModal"
      @close="showWhyPixModal = false"
    />
  </section>
</template>

<style lang="scss" module>

.pixTutorial {
  display: flex;
  align-items: center;
  flex-direction: column;

  .navButton {
    --button-size: 5.6rem;
    font-size: var(--font-size-large);

    &.prev {
      margin-right: var(--space-regular);
    }

    &.next {
      margin-left: var(--space-regular);
    }
  }
}

.carousel {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: var(--space-medium) 0;

  .carouselImages {
    display: flex;
    align-items: center;
    margin-bottom: var(--space-regular);
  }

  .carouselFooter {
    display: flex;
    align-items: center;
    flex-direction: column;
    text-align: center;
  }

  .dotsContainer {
    display: flex;
    justify-content: center;
    gap: var(--space-small-xx);

    .dot {
      --size: 8px;

      width: var(--size);
      height: var(--size);
      background-color: var(--color-neutral-600);
      border-radius: 50%;
    }

    .activeDot {
      background-color: var(--color-neutral-1000);
    }
  }
}

.actionButton {
  margin-bottom: var(--space-large);
}

.helpButton {
  margin-bottom: var(--space-regular);
}

@media (min-width: 768px) {
  .carouselFooter {
    width: 450px;
  }
}

</style>
